import dayjs from 'dayjs';
import { View } from '@react-pdf/renderer';
import { DateUtils, Invoice, Supplier } from '../../../common';
import { en } from '../../theme/languages/en';
import { Span } from '../Span/Span';
import { DATE_FORMAT_SLASH } from '../../utils/invoiceTable';
import { styles } from './DebitMetaData.module';
import { getInvoiceIdName, getNetTermLabel } from '../../utils';

interface DebitMetaDataProps {
  debitData: Invoice;
  title: string;
  className?: {};
  showBillingPeriod?: boolean;
}

export const DebitMetaData = ({ title, debitData, className, showBillingPeriod }: DebitMetaDataProps) => {
  const dueDate =
    debitData?.customer &&
    dayjs(
      DateUtils.getDueDate(
        debitData?.issueDate || new Date(),
        isNaN(+debitData?.netTerms) ? debitData?.netTerms : +debitData?.netTerms,
        debitData?.customer?.country,
      ),
    ).format(DATE_FORMAT_SLASH);

  return (
    <View style={className}>
      <View style={styles.invoiceDetails}>
        {debitData?.poNumber && <Span style={styles.po}>{debitData?.poNumber}</Span>}
        <Span style={styles.mainTitle} canBeMissingValue missingValue={title}>
          {title}
        </Span>

        <View style={styles.detailsContainer}>
          <View style={styles.detailsSection}>
            <Span style={styles.detailsTitle}>{en.billTo}</Span>
            <Span style={styles.text}>{debitData?.customer?.legalName}</Span>
            <Span style={styles.text}>{debitData?.customer?.billingAddress}</Span>
            <Span style={styles.text}>{debitData?.customer?.city}</Span>
            <Span style={styles.text}>{debitData?.customer?.countryState}</Span>
            <Span style={styles.text}>{debitData?.customer?.country}</Span>
          </View>

          <View style={[styles.detailsSection, { marginLeft: 80 }]}>
            <View style={styles.textContainer}>
              <Span style={styles.detailsTitle}>{en.debitNo}</Span>
              <Span
                style={styles.text}
                canBeMissingValue
                missingValue={debitData?.documentNumber || debitData?.draftNumber}
                placeholder={en.draft}
              >
                {getInvoiceIdName(debitData)}
              </Span>
            </View>

            <View style={styles.textContainer}>
              <Span style={styles.text}>{`${en.issueDate}:`}</Span>
              <Span style={styles.text} canBeMissingValue missingValue={debitData?.issueDate} placeholder={'-'}>
                {dayjs(debitData?.issueDate).format(DATE_FORMAT_SLASH)}
              </Span>
            </View>

            <View style={styles.textContainer}>
              <Span style={styles.text}>{`${en.dueDate}:`}</Span>
              <Span style={styles.text} canBeMissingValue missingValue={dueDate} placeholder={'-'}>
                {dueDate}
                {!!+debitData?.netTerms && (
                  <Span style={styles.text} canBeMissingValue missingValue={debitData?.netTerms}>
                    {getNetTermLabel(debitData?.netTerms)}
                  </Span>
                )}
              </Span>
            </View>

            {showBillingPeriod && (
              <View style={styles.textContainer}>
                <Span style={styles.text}>{`${en.billingPeriod}:`}</Span>
                <Span
                  style={styles.text}
                  canBeMissingValue
                  missingValue={
                    debitData?.pricingModel?.tabs[0]?.billingStartDate ||
                    debitData?.pricingModel?.tabs[0]?.billingEndDate
                  }
                  placeholder={'-'}
                >{`${dayjs(debitData?.pricingModel?.tabs[0]?.billingStartDate).format(DATE_FORMAT_SLASH)} - ${dayjs(
                  debitData?.pricingModel?.tabs[0]?.billingEndDate,
                ).format(DATE_FORMAT_SLASH)}`}</Span>
              </View>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};
